import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { Globals } from '../../globals';
import { ControllerPrefix } from '../networkdevices/networkdevices.interfaces';
import { Order } from '../orders/orders.service';
import { Voucher } from '../vouchers/vouchers.service';
import { PaymentResult } from './payment-result.interface';

@Injectable({
  providedIn: 'root',
})
export class ProductsService {
  private readonly client = inject(HttpClient);
  private readonly globals = inject(Globals);

  /**
   * Get products by name
   * @param name
   * @returns
   */
  getProductsByName(name: string): Observable<Product[]> {
    return this.client.get<Product[]>(
      `${this.globals.apiRoot}/jwtproducts/name/${name}`,
    );
  }

  getLuqasProducts(): Observable<Product[]> {
    return this.client.get<Product[]>(
      `${this.globals.apiRoot}/jwtproducts/luqas`,
    );
  }

  getProductByClientId(clientId: string): Observable<Product> {
    return this.client.get<Product>(
      this.globals.apiRoot + '/jwtproducts/public/' + clientId,
    );
  }

  /**
   * Get all payment products (no packs) owned by the current user for the current app (admin/client) - bought and free products
   */
  getOwnedPaymentProductsBySite(): Observable<Product[]> {
    const url = `${this.globals.apiRoot}/jwtProducts/owned/${this.globals.appOrigin}/payment`;
    return this.client.get<Product[]>(url);
  }

  /**
   * Get all products owned by the current user for the current app (admin/client) - bought and free products
   */
  getOwnedProductsBySite(): Observable<OwnedProduct[]> {
    const url = `${this.globals.apiRoot}/jwtProducts/owned/${this.globals.appOrigin}`;
    return this.client.get<OwnedProduct[]>(url);
  }

  /**
   * Get all products that can be bought for the current app (admin/client)
   */
  getProductsBySite(): Observable<Product[]> {
    const url = `${this.globals.apiRoot}/jwtProducts/shop/${this.globals.appOrigin}`;
    return this.client.get<Product[]>(url);
  }

  /**
   * Get all payment products that can be bought for the current app (admin/client)
   */
  getPaymentProductsBySite(): Observable<Product[]> {
    const url = `${this.globals.apiRoot}/jwtProducts/shop/${this.globals.appOrigin}/payment`;
    return this.client.get<Product[]>(url);
  }

  /**
   * Get all products that can be bought for the current app (admin/client)
   */
  getProductsToBuyWithPaymentProductsBySite(): Observable<Product[]> {
    const url = `${this.globals.apiRoot}/jwtProducts/shop/${this.globals.appOrigin}/buywithpaymentproducts`;
    return this.client.get<Product[]>(url);
  }

  /**
   * Buy a product
   */
  buyProduct(
    uid: string,
    amount: number,
    info: PaymentInfo,
  ): Observable<PaymentInfo> {
    const url = `${this.globals.apiRoot}/jwtProducts/${uid}/buy/${amount}`;
    return this.client.post<PaymentInfo>(url, info);
  }

  /**
   * create order and pass the paymentlink
   * @param controllerId
   * @param applicationUid
   * @returns paymentResult with paymentLink if succeeded, otherwise message
   */
  payProductByUid(
    controllerId: number,
    applicationUid: string,
  ): Observable<PaymentResult> {
    const url = `${this.globals.apiRoot}/jwtProducts/pay`;
    // jwtapi/jwtProducts/{applicationUid}/pay/{controllerId}
    return this.client.post<PaymentResult>(url, {
      applicationUid,
      controllerId,
    });
  }

  getOwnedProducts() {
    const url = `${this.globals.apiRoot}/jwtUsers/me/products`;
    return this.client.get<ProductOwner[]>(url);
  }

  buyIdbLicense(
    productUid: string,
    installationId: number,
  ): Observable<{ paymentUrl: string }> {
    return this.client.post<{ paymentUrl: string }>(
      `${this.globals.apiRoot}/jwtProducts/idb/${productUid}/buy`,
      { installationId },
    );
  }

  getInstallerInventory(): Observable<Product[]> {
    return this.client.get<Product[]>(
      `${this.globals.apiRoot}/jwtProducts/installer/inventory`,
    );
  }

  buyIdbLicenseToStock(productUid: string): Observable<{ paymentUrl: string }> {
    return this.client.post<{ paymentUrl: string }>(
      `${this.globals.apiRoot}/jwtProducts/installer/stock/buy?productUid=${productUid}`,
      null,
    );
  }

  assignLicenseFromStock(
    productUid: string,
    installationId: number,
  ): Observable<void> {
    return this.client.post<void>(
      `${this.globals.apiRoot}/jwtProducts/installer/stock/assign`,
      { productUid, installationId },
    );
  }
}

export interface ProductCategory {
  name?: string;
  controllerPrefix?: ControllerPrefix;
}

export interface Product {
  uid?: string;
  name: string;
  unit?: string;
  defaultProduct?: boolean;
  recurring?: boolean;
  price?: number;
  pricePaymentProduct?: number;
  paymentProductId?: number;
  amount?: number;
  clientId?: string;
  clientSecret?: string;
  id?: number;
  title?: string;
  info?: string;
  monthsValid?: number;
  availableItems?: number;
  category?: ProductCategory;
  order?: Order;
}

export interface PaymentInfo {
  paymentUrl?: string;
  redirectUrl?: string;
  orderDescription?: string;
  vouchers?: Voucher[];
}

export interface OwnedProduct {
  name: string;
  price?: number;
  unit?: string;
  order?: Order;
  installationName?: string;
  licenseStatus?: 'assigned' | 'unassigned' | 'expired';
}

export interface ProductOwner {
  id: number;
  name: string;
}
